import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { SEOHead } from "@/features/shared/SEOHead";
import { VisibilityAuditWidget } from "@/components/VisibilityAuditWidget";
import { CheckCircle2, Gauge, Search, MapPin, Star, ListChecks } from "lucide-react";

export default function DigitalAudit() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Free Website & SEO Audit | Frayze"
        description="Enter your business and get a free, instant report on your website speed, SEO, local search visibility, reputation, and business listings: no call, no credit card."
        ogDescription="Free instant audit of your website speed, SEO, local search, reputation, and listings. See exactly where you're losing customers online."
        canonical="https://frayze.ca/digital-audit/"
        keywords="free website audit, SEO audit, local SEO check, online presence audit, reputation check, business listings audit, Frayze"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
      />
      <Header />

      <main className="pt-40 pb-16">
        <div className="container px-4 mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Left Column: Context */}
            <div className="space-y-8 lg:sticky lg:top-32">
              <div className="space-y-4">
                <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-primary text-primary-foreground">
                  Free Instant Audit
                </div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                  See where your online presence is leaking
                </h1>
                <p className="text-xl text-muted-foreground">
                  Enter your business and get a free, instant report: no call, no credit card. Just a
                  clear picture of what's helping you win customers online and what's quietly costing you.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg">What the report checks:</h3>
                <ul className="space-y-3">
                  {[
                    { icon: Gauge, label: "Website speed & performance" },
                    { icon: Search, label: "SEO & search visibility" },
                    { icon: MapPin, label: "Local search & map presence" },
                    { icon: Star, label: "Reputation & reviews" },
                    { icon: ListChecks, label: "Business listings accuracy" },
                  ].map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-muted/30 rounded-2xl border">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="font-semibold">Free, instant, no obligation</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  We'll show your score on screen and email you the full breakdown. If you want help
                  fixing what it finds, we're a 15-minute call away: but there's no pressure to.
                </p>
              </div>
            </div>

            {/* Right Column: The real audit */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-3xl opacity-10 pointer-events-none" />
              <div className="relative z-10 bg-background rounded-2xl border p-2 sm:p-4 md:p-6 overflow-hidden">
                <VisibilityAuditWidget />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <StickyCTA />
    </div>
  );
};
